import { z } from "zod";

export const TaskTypeValues = [
  "form",
  "fill_document",
  "sign_document",
  "custom_task",
  "custom_field",
] as const;

export type TaskType = (typeof TaskTypeValues)[number];

export const TaskCompletionMethodValues = ["object_update", "manual"] as const;

export type TaskCompletionMethod = (typeof TaskCompletionMethodValues)[number];

export const TaskStatusValues = ["incomplete", "in_progress", "complete", "dismissed"] as const;

export type TaskStatus = (typeof TaskStatusValues)[number];

/** For tasks done across a set of records, e.g. 3 of 11 jobs have a location. */
export type TaskProgress = { done: number; total: number };

export const TaskObjectTypeValues = [
  "I9",
  "FillableTemplate",
  "File",
  "Form",
  "FormSubmission",
  "Certification",
  "CertificationType",
  "CustomField",
  "CustomFieldValue",
  "CustomTask",
  "Screening",
  "BankAccount",
  "OfferLetter",
  "ESignatureItem",
  "SafetyAction",
  "Observation",
  "Expense",
  "ExpenseReimbursement",
  "TimeOffRequest",
  "SafetyMeeting",
  "Timesheet",
  "TeamMemberChangeRequest",
  "Bill",
  "DailyReport",
  "PeerNominationSubmission",
  "TeamMemberPerformanceReview",
  "Writeup",
] as const;

export type TaskObjectType = (typeof TaskObjectTypeValues)[number];

export const TaskSubjectValues = ["company", "team_member"] as const;

export type TaskSubject = (typeof TaskSubjectValues)[number];

// A task source is the processor that owns a family of tasks and subscribes to
// their domain events, not the shape of an individual task -- that's TaskType.
export const TASK_SOURCE_VALUES = ["launch_onboarding"] as const;

export type TaskSource = (typeof TASK_SOURCE_VALUES)[number];

// Tasks store these keys, so never remove one: the validators plugin would reject every later
// update to a task holding it. Retire a key by pointing it at a new page in the dashboard instead.
export const TASK_DESTINATION_VALUES = [
  "safety_settings",
  "safety_notification_settings",
  "safety_meetings",
  "safety_meeting_templates",
  "safety_checklists",
  "safety_checklist_templates",
  "safety_incidents",
  "safety_incident_settings",
  "safety_hazards_and_behaviors",
  "safety_passport_settings",
  "safety_meeting_setup",
  "safety_checklist_setup",
  "safety_incident_setup",
  "safety_profile_settings",
  "safety_passport_setup",
  "safety_profile_setup",
  "per_diem_rates",
  "per_diem_settings",
  "jobs",
  "team_members",
  "integrations",
  "vendors",
  "bank_accounts",
  "accounts_payable",
  "bills_to_approve",
  "bills_to_pay",
  "accounts_payable_settings",
  "bill_email_forwarding",
  "bill_custom_fields",
  "performance_templates",
  "performance_review_cycles",
  "performance_feedback",
  "card_programs",
  "card_transactions",
  "card_transactions_to_approve",
  "reimbursement_settings",
  "reimbursements",
  "reimbursements_to_approve",
  "project_intelligence_data_source_setup",
  "project_intelligence_sync_setup",
  "project_intelligence_cost_type_setup",
  "project_intelligence_permissions_setup",
  "permission_groups",
  "cost_type_settings",
] as const;

/** The dashboard page where a task's work happens. The dashboard maps each key to a route. */
export type TaskDestination = (typeof TASK_DESTINATION_VALUES)[number];

export const DueDateRelativeToValues = ["start_date", "end_date"] as const;

export type DueDateRelativeTo = (typeof DueDateRelativeToValues)[number];

/**
 * Due date configuration supporting both static ISO date strings
 * and relative date configurations.
 */
export type DueDate =
  | string
  | {
      relative_to: DueDateRelativeTo;
      offset_days: number;
      business_days?: boolean;
    };

export const DueDateZodSchema = z.union([
  z.string(),
  z.object({
    relative_to: z.enum(DueDateRelativeToValues),
    offset_days: z.number(),
    business_days: z.boolean().optional(),
  }),
]) satisfies z.ZodType<DueDate>;
